import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_FTAPI_URL || 'https://ftapi.twodots.cloud';
const API_KEY = process.env.REACT_APP_FTAPI_API_KEY || '';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(API_KEY && { 'x-api-key': API_KEY }),
  },
});

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('google_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - clear token
      localStorage.removeItem('google_token');
      // Add a more helpful error message
      if (error.response?.data?.error) {
        error.message = error.response.data.error;
      } else {
        error.message = 'Authentication required. Please sign in with Google.';
      }
    } else if (error.response?.status === 403) {
      error.message = error.response?.data?.error || 'Access denied. Check your API key.';
    } else if (!error.response) {
      error.message = 'Network error. Please check your connection.';
    } else if (error.response?.data?.error) {
      error.message = error.response.data.error;
    }
    return Promise.reject(error);
  }
);

export default api;

// API methods
export const familiesAPI = {
  list: () => api.get('/api/ft/families'),
  get: (id) => api.get(`/api/ft/families/${id}`),
  create: (data) => api.post('/api/ft/families', data),
  update: (id, data) => api.patch(`/api/ft/families/${id}`, data),
  delete: (id) => api.delete(`/api/ft/families/${id}`),
  joinByCode: (joinCode) => api.post('/api/ft/families/join', { join_code: joinCode }),
};

export const peopleAPI = {
  list: (familyId) => api.get(`/api/ft/families/${familyId}/people`),
  get: (personId) => api.get(`/api/ft/people/${personId}`),
  getWithRelationships: (personId) => api.get(`/api/ft/people/${personId}/with-relationships`),
  create: (familyId, data) => api.post(`/api/ft/families/${familyId}/people`, data),
  update: (personId, data) => api.patch(`/api/ft/people/${personId}`, data),
  delete: (personId) => api.delete(`/api/ft/people/${personId}`),
  claim: (personId) => api.post(`/api/ft/people/${personId}/claim`),
  uploadPicture: (personId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/ft/people/${personId}/upload-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const membershipsAPI = {
  list: (familyId) => api.get(`/api/ft/families/${familyId}/memberships`),
  upsert: (familyId, userId, data) => api.put(`/api/ft/families/${familyId}/memberships/${userId}`, data),
  delete: (familyId, userId) => api.delete(`/api/ft/families/${familyId}/memberships/${userId}`),
};

export const relationshipsAPI = {
  parentChild: {
    list: (familyId) => api.get(`/api/ft/families/${familyId}/parent-child`),
    create: (familyId, data) => api.post(`/api/ft/families/${familyId}/parent-child`, data),
    update: (familyId, parentId, childId, data) => api.patch(`/api/ft/families/${familyId}/parent-child/${parentId}/${childId}`, data),
    delete: (familyId, parentId, childId) => api.delete(`/api/ft/families/${familyId}/parent-child/${parentId}/${childId}`),
  },
  partnerships: {
    list: (familyId) => api.get(`/api/ft/families/${familyId}/partnerships`),
    create: (familyId, data) => api.post(`/api/ft/families/${familyId}/partnerships`, data),
    update: (partnershipId, data) => api.patch(`/api/ft/partnerships/${partnershipId}`, data),
    delete: (partnershipId) => api.delete(`/api/ft/partnerships/${partnershipId}`),
  },
};

export const userAPI = {
  me: () => api.get('/api/ft/me'),
  topContributors: () => api.get('/api/ft/top-contributors'),
  mostActiveUsers: () => api.get('/api/ft/most-active-users'),
};

export const hobbiesAPI = {
  list: (familyId) => api.get(`/api/ft/families/${familyId}/hobbies`),
  create: (familyId, data) => api.post(`/api/ft/families/${familyId}/hobbies`, data),
  delete: (hobbyId) => api.delete(`/api/ft/hobbies/${hobbyId}`),
  addToPerson: (personId, hobbyId) => api.post(`/api/ft/people/${personId}/hobbies/${hobbyId}`),
  removeFromPerson: (personId, hobbyId) => api.delete(`/api/ft/people/${personId}/hobbies/${hobbyId}`),
};

export const socialLinksAPI = {
  list: (personId) => api.get(`/api/ft/people/${personId}/social-links`),
  create: (personId, data) => api.post(`/api/ft/people/${personId}/social-links`, data),
  update: (linkId, data) => api.patch(`/api/ft/social-links/${linkId}`, data),
  delete: (linkId) => api.delete(`/api/ft/social-links/${linkId}`),
};

export const contactsAPI = {
  list: (personId) => api.get(`/api/ft/people/${personId}/contacts`),
  create: (personId, data) => api.post(`/api/ft/people/${personId}/contacts`, data),
  update: (contactId, data) => api.patch(`/api/ft/contacts/${contactId}`, data),
  delete: (contactId) => api.delete(`/api/ft/contacts/${contactId}`),
};

